import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../Context/TenantContext';
import { UserContext } from '../BasicComponents/UserContext';
import API_BASE_URL from '../Config/config';
import {
  BookOpenIcon,
  RocketLaunchIcon,
  CodeBracketIcon,
  BriefcaseIcon,
  SparklesIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  FireIcon,
  ClockIcon,
  CheckCircleIcon as CheckCircleOutline,
  BellIcon,
  PresentationChartLineIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, ChevronRightIcon } from '@heroicons/react/24/solid';

/**
 * TenantDashboardHome
 *
 * Personalized, premium AI-powered dashboard for Skill Genie students.
 * Featuring the Royal Purple design system and intelligent command center.
 */
const TenantDashboardHome = () => {
  const navigate = useNavigate();
  const { getCollegeName } = useTenant();
  const { user } = useContext(UserContext);
  const [ready, setReady] = useState(false);

  // --- Dynamic Data State ---
  const [techProgress, setTechProgress] = useState({});
  const [lastRead, setLastRead] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [bootcampData, setBootcampData] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [learningStats, setLearningStats] = useState({
    hours: 0,
    topics: 0,
    streak: 0,
    xp: 0,
    compilerRuns: 0,
    aiSessions: 0,
    mockInterviews: 0
  });

  const studentName =
    user?.name ||
    user?.first_name ||
    user?.username ||
    user?.email?.split('@')[0] ||
    'Learner';

  useEffect(() => {
    const loadDashboardData = async () => {
      // 1. Basic Local Data
      const savedLastRead = localStorage.getItem('last_read_doc');
      if (savedLastRead) setLastRead(JSON.parse(savedLastRead));

      const activity = JSON.parse(localStorage.getItem('user_activity') || "[]");
      setRecentActivity(activity);

      // 2. Fetch Content Map for Progress calculation
      try {
        const response = await fetch('/contentMap.json');
        const contentMapData = await response.json();
        
        const techs = ['AI', 'AWS', 'React', 'Java', 'Python'];
        const progressMap = {};
        let totalCompleted = 0;
        
        techs.forEach(tech => {
          const completedKey = `completed_docs_${tech}`;
          const completed = JSON.parse(localStorage.getItem(completedKey) || "[]");
          totalCompleted += completed.length;
          
          const techData = contentMapData.find(item => 
            item.technology?.toLowerCase() === tech.toLowerCase()
          );
          
          let totalDocs = 0;
          if (techData && techData.topics) {
            techData.topics.forEach(topic => {
              if (topic.subtopics) totalDocs += topic.subtopics.length;
            });
          }
          
          progressMap[tech] = {
            completed: completed.length,
            total: totalDocs || 1,
            percent: Math.round((completed.length / (totalDocs || 1)) * 100),
            name: tech
          };
        });
        
        setTechProgress(progressMap);

        // 3. Generate Recommendations (Logic from Dashboard.jsx)
        const recs = [];
        const sortedTechs = Object.entries(progressMap)
          .filter(([_, data]) => data.percent < 100)
          .sort((a, b) => b[1].percent - a[1].percent);
        
        if (sortedTechs.length > 0) {
          recs.push({
            name: sortedTechs[0][0],
            reason: `Continue your ${sortedTechs[0][0]} journey`,
            difficulty: 'Medium',
            time: '6 Hours',
            impact: 'High'
          });
        }
        
        // Add some defaults for recommendation UI
        ['Node.js', 'Express.js', 'MongoDB'].forEach(t => {
          if (recs.length < 3) {
            recs.push({
              name: t,
              reason: `Complements your existing skills`,
              difficulty: 'Medium',
              time: '8 Hours',
              impact: 'High'
            });
          }
        });
        setRecommendations(recs);

        // 4. Update Stats
        setLearningStats(prev => ({
          ...prev,
          topics: totalCompleted,
          hours: Math.round(activity.length * 0.5),
          streak: Math.min(activity.length, 5) || 1,
          xp: totalCompleted * 50 + (activity.length * 10),
          compilerRuns: activity.filter(a => a.type === 'compiler').length || 12,
          aiSessions: activity.filter(act => act.event?.includes('AI')).length || 8,
          mockInterviews: activity.filter(act => act.event?.includes('Interview')).length || 2
        }));

      } catch (err) {
        console.error("Dashboard data load failed:", err);
      }

      // 5. Bootcamp Enrollment
      const userId = user?.userId || user?.id || localStorage.getItem("user_id");
      if (userId) {
        try {
          const res = await fetch(`${API_BASE_URL}/api/bootcamp/enrollments/my-bootcamps?userId=${userId}`);
          const data = await res.json();
          if (data && data.enrollments && data.enrollments.length > 0) {
            setBootcampData(data.enrollments[0]);
          }
        } catch (e) {}
      }

      setReady(true);
    };

    loadDashboardData();
  }, [user]);

  // Personalized Welcome Message Components
  const currentTech = lastRead?.tech || 'Full Stack Web Development';
  const completedTechCount = Object.values(techProgress).filter(t => t.percent === 100).length;
  const recommendedNext = recommendations[0]?.name || 'Node.js';

  if (!ready) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <div className="w-12 h-12 border-4 border-purple-200 border-t-[#6A0DAD] rounded-full animate-spin" />
        <p className="text-sm font-bold text-[#6B7280] animate-pulse">Initializing your command center...</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1200px] mx-auto space-y-8 pb-20 animate-fade-in px-4 sm:px-6">
      
      {/* 1. Hero Welcome Section - Redesigned to Premium Enterprise Grade */}
      <section className="relative overflow-hidden rounded-[32px] bg-gradient-to-br from-[#1E1B4B] via-[#5B21B6] to-[#7C3AED] p-6 sm:p-8 text-white shadow-[0_20px_40px_rgba(30,27,75,0.2)] group">
        {/* Animated Background Orbs */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-[100px] -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-500/10 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative z-10 flex flex-col md:flex-row items-start justify-between gap-8">
          {/* Left Column: Greeting & Action */}
          <div className="flex-1 space-y-4">
            <div className="space-y-2 text-left">
              {/* Learning Streak Badge */}
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/10 backdrop-blur-md rounded-full border border-white/10 mb-1 -ml-1">
                <FireIcon className="h-3 w-3 text-orange-400" />
                <span className="text-[7px] font-black uppercase tracking-[0.2em]">
                  Learning Streak: {learningStats.streak} {learningStats.streak === 1 ? 'Day' : 'Days'}
                </span>
              </div>

              <h1 className="text-xl sm:text-3xl font-black tracking-tight leading-tight block text-white -ml-1">
                Hi <span className="text-white">{studentName}</span> 👋
              </h1>
              
              <p className="text-sm text-purple-100/70 font-medium max-w-lg">
                Continue your learning journey with confidence. Every lesson completed brings you one step closer to your dream career.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button 
                onClick={() => navigate('/techgrid')}
                className="bg-white text-[#1E1B4B] px-6 py-2.5 rounded-xl font-black hover:scale-105 hover:shadow-[0_15px_30px_rgba(255,255,255,0.2)] transition-all flex items-center gap-2 shadow-lg active:scale-95 group/btn text-[10px]"
              >
                <RocketLaunchIcon className="h-4 w-4 group-hover/btn:animate-bounce" />
                Start Learning
              </button>
              <button 
                onClick={() => navigate('/student/tsg')}
                className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-6 py-2.5 rounded-xl font-black hover:bg-white/20 transition-all flex items-center gap-2 active:scale-95 text-[10px]"
              >
                <SparklesIcon className="h-4 w-4" />
                AI Tutor
              </button>
            </div>
          </div>

          {/* Right Column: Today's Focus Card */}
          <div className="flex-1 w-full lg:max-w-md animate-fade-in" style={{ animationDelay: '200ms' }}>
            <div className="relative group/panel">
              <div className="relative z-10 bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-5 shadow-2xl transition-all duration-500 hover:bg-white/15 hover:shadow-purple-500/20 hover:-translate-y-1">
                <div className="space-y-3">
                  <div>
                    <h3 className="text-base font-black tracking-tight text-white mb-0.5">Today's Focus</h3>
                    <p className="text-[10px] text-purple-100/70 font-medium">Continue your learning journey and build momentum.</p>
                  </div>

                  <div className="grid gap-2">
                    {[
                      { label: 'Continue Learning', icon: BookOpenIcon, desc: 'Pick up where you left off and maintain consistency.' },
                      { label: 'Complete Assessments', icon: ClipboardDocumentListIcon, desc: 'Validate your understanding through guided evaluations.' },
                      { label: 'Practice Coding', icon: CodeBracketIcon, desc: 'Strengthen practical skills through hands-on exercises.' },
                      { label: 'Prepare for Interviews', icon: BriefcaseIcon, desc: 'Build confidence with structured interview preparation.' }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2.5 group/item cursor-pointer">
                        <div className="flex-shrink-0 w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center border border-white/10 group-hover/item:bg-white/20 transition-colors">
                          <item.icon className="h-3.5 w-3.5 text-purple-200" />
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <CheckCircleIcon className="h-3 w-3 text-emerald-400" />
                            <span className="text-[10px] font-bold text-white group-hover/item:text-purple-200 transition-colors">{item.label}</span>
                          </div>
                          <p className="text-[8px] text-purple-100/50">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="pt-2 border-t border-white/10">
                    <p className="text-[8px] text-purple-100/60 font-medium italic leading-relaxed">"Every small step compounds into long-term career growth."</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Left Column (8 units) */}
        <div className="lg:col-span-8 space-y-6">
          
          {/* 4. Learning Summary Card */}
          <section className="bg-white rounded-[24px] p-8 shadow-sm border border-slate-200/60 hover:border-purple-200 transition-colors">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-lg font-black text-[#111827] flex items-center gap-2">
                <PresentationChartLineIcon className="h-5 w-5 text-[#6A0DAD]" />
                Learning Summary
              </h3>
              <span className="text-[10px] font-black text-[#6A0DAD] bg-purple-50 px-3 py-1 rounded-full uppercase tracking-widest">Live Progress</span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
              <div className="space-y-6">
                <div>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Current Technology</p>
                  <p className="text-lg font-black text-[#111827]">{currentTech}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Level</p>
                    <p className="text-base font-black text-[#111827]">{lastRead?.level || 1} / 15</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Topic</p>
                    <p className="text-base font-black text-[#111827] leading-tight">{lastRead?.title || 'Getting Started'}</p>
                  </div>
                </div>
                <div>
                  <div className="flex justify-between items-end mb-2">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Completion</p>
                    <p className="text-xl font-black text-[#6A0DAD]">{lastRead?.progress || 0}%</p>
                  </div>
                  <div className="h-2 w-full bg-purple-50 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#6A0DAD] to-[#7B2CBF] transition-all duration-1000" style={{ width: `${lastRead?.progress || 0}%` }}></div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 bg-slate-50/80 p-6 rounded-[20px] border border-slate-100">
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Time Spent</p>
                  <p className="text-base font-black text-[#111827]">{learningStats.hours} Hours</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Remaining</p>
                  <p className="text-base font-black text-[#111827]">3 Topics</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Est. Time</p>
                  <p className="text-base font-black text-[#111827]">25 Minutes</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Path</p>
                  <p className="text-base font-black text-[#6A0DAD]">{bootcampData?.bootcampName?.split(' ')[0] || 'Standard'}</p>
                </div>
              </div>
            </div>
          </section>

          {/* 5. AI Recommended Technologies */}
          <section className="space-y-6">
            <h2 className="text-xl font-black text-[#111827]">AI Recommended Technologies</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {recommendations.map((rec, i) => (
                <div key={i} className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-200/60 hover:shadow-xl hover:-translate-y-1 hover:border-[#6A0DAD]/30 transition-all group">
                  <div className="flex justify-between items-start mb-4">
                    <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center text-[#6A0DAD] group-hover:bg-[#6A0DAD] group-hover:text-white transition-colors">
                      <SparklesIcon className="h-5 w-5" />
                    </div>
                    <span className="text-[9px] font-black bg-emerald-50 text-emerald-600 px-2 py-1 rounded-lg uppercase tracking-wider">{rec.impact} Impact</span>
                  </div>
                  <h3 className="text-lg font-black text-[#111827] mb-2">{rec.name}</h3>
                  <p className="text-xs font-medium text-gray-500 mb-4 line-clamp-2">
                    <span className="font-bold text-[#6A0DAD]">Reason:</span> {rec.reason}
                  </p>
                  <div className="space-y-2 mb-6">
                    <div className="flex justify-between text-[10px] font-bold text-gray-400">
                      <span>Difficulty</span>
                      <span className="text-orange-500">{rec.difficulty}</span>
                    </div>
                    <div className="flex justify-between text-[10px] font-bold text-gray-400">
                      <span>Est. Time</span>
                      <span className="text-[#111827]">{rec.time}</span>
                    </div>
                  </div>
                  <button onClick={() => navigate('/techgrid')} className="w-full py-2.5 rounded-xl bg-purple-50 text-[#6A0DAD] font-black hover:bg-[#6A0DAD] hover:text-white transition-all text-xs">
                    Start Learning
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* 6. Continue Learning (Alternate Layout) */}
          <section className="bg-gradient-to-r from-[#F8F7FF] to-white rounded-[24px] p-8 border border-slate-200/60 hover:border-purple-200 transition-colors flex flex-col md:flex-row items-center gap-8 shadow-sm">
            <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-purple-50 flex items-center justify-center flex-shrink-0">
               <RocketLaunchIcon className="h-8 w-8 text-[#6A0DAD]" />
            </div>
            <div className="flex-1 space-y-1 text-center md:text-left">
              <h3 className="text-xl font-black text-[#111827]">{lastRead?.title || 'React Hooks'}</h3>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">{currentTech} • Level {lastRead?.level || 7}</p>
              <div className="flex items-center gap-4 pt-2 max-w-md mx-auto md:mx-0">
                <div className="flex-1 h-1.5 bg-white rounded-full overflow-hidden border border-purple-50">
                  <div className="h-full bg-[#6A0DAD]" style={{ width: `${lastRead?.progress || 72}%` }}></div>
                </div>
                <span className="text-[10px] font-black text-[#6A0DAD]">{lastRead?.progress || 72}%</span>
              </div>
              <p className="text-[10px] font-medium text-gray-400 pt-1">Remaining: 2 Topics • Est. Time: 20 Minutes</p>
            </div>
            <button 
              onClick={() => navigate('/techgrid')}
              className="bg-[#6A0DAD] text-white px-8 py-3 rounded-[12px] text-sm font-black hover:scale-105 transition-all shadow-lg"
            >
              Resume
            </button>
          </section>

          {/* 7. Recently Learned Technologies */}
          <section className="space-y-6">
            <h2 className="text-xl font-black text-[#111827]">Recently Learned</h2>
            <div className="flex flex-wrap gap-3">
              {['HTML', 'CSS', 'JavaScript', 'SQL', 'Git', 'React', 'Node'].map((tech, i) => (
                <div 
                  key={i} 
                  onClick={() => navigate('/techgrid')}
                  className="flex-shrink-0 bg-white px-5 py-3 rounded-[16px] shadow-sm border border-slate-200/60 flex items-center gap-3 hover:border-[#6A0DAD]/40 transition-all cursor-pointer group"
                >
                  <div className="w-6 h-6 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-colors">
                    <CheckCircleIcon className="h-4 w-4" />
                  </div>
                  <span className="text-sm font-black text-[#111827]">{tech}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 8. Weekly Learning Activity */}
          <section className="bg-white rounded-[24px] p-8 shadow-sm border border-slate-200/60 hover:border-purple-200 transition-colors">
            <div className="flex items-center justify-between mb-10">
              <h3 className="text-lg font-black text-[#111827]">Weekly Activity</h3>
              <div className="flex gap-4">
                 <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400">
                    <span className="w-2.5 h-2.5 bg-[#6A0DAD] rounded-full"></span> Topics
                 </div>
                 <div className="flex items-center gap-2 text-[10px] font-bold text-gray-400">
                    <span className="w-2.5 h-2.5 bg-purple-200 rounded-full"></span> Hours
                 </div>
              </div>
            </div>
            <div className="flex items-end justify-between h-40 gap-4 px-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => {
                const height = [60, 90, 40, 70, 30, 100, 20][i];
                return (
                  <div key={i} className="flex-1 flex flex-col items-center gap-3">
                    <div className="w-full relative group">
                      <div className="h-32 w-full bg-slate-50 rounded-[10px] relative overflow-hidden">
                        <div 
                          className="absolute bottom-0 w-full bg-gradient-to-t from-[#6A0DAD] to-[#7B2CBF] rounded-t-[10px] transition-all duration-1000"
                          style={{ height: `${height}%` }}
                        ></div>
                      </div>
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#111827] text-white text-[9px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20">
                        {Math.round(height/10)} Topics
                      </div>
                    </div>
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{day}</span>
                  </div>
                )
              })}
            </div>
          </section>
        </div>

        {/* Right Column (4 units) */}
        <div className="lg:col-span-4 space-y-8">

          {/* 15. Elite Mastery Feature - No Restrictions */}
          <section className="bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 rounded-[24px] p-8 shadow-lg border-2 border-amber-200 hover:shadow-xl hover:border-amber-300 transition-all relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-300/20 to-orange-400/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:scale-110 transition-transform"></div>

            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-1">
                <div className="px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[9px] font-black rounded-full uppercase tracking-widest shadow-md">
                  Elite Status Unlocked
                </div>
              </div>

              <h3 className="text-2xl font-black text-amber-900 mb-1">Elite Mastery</h3>
              <p className="text-xs font-bold text-amber-700/70 mb-6">Access premium features without any restrictions</p>

              <div className="space-y-4">
                {/* Mastery Level */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-amber-100 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[10px] font-black text-amber-700 uppercase tracking-widest">Mastery Level</span>
                    <span className="text-lg font-black text-amber-600">Expert</span>
                  </div>
                  <div className="h-2.5 w-full bg-amber-100 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 w-full"></div>
                  </div>
                  <p className="text-[9px] text-amber-600 font-bold mt-2">100% - Maximum Proficiency Achieved</p>
                </div>

                {/* Restricted Features Unlocked */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-amber-100 shadow-sm">
                  <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-3">Unrestricted Access</p>
                  <div className="space-y-2">
                    {[
                      { feature: 'Advanced AI Mentoring', icon: '🤖' },
                      { feature: 'Unlimited Code Reviews', icon: '✨' },
                      { feature: 'Premium Interview Prep', icon: '🎯' },
                      { feature: 'Career Guidance', icon: '📈' },
                      { feature: 'Priority Support', icon: '⚡' }
                    ].map((item, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs font-bold text-amber-900">
                        <span className="text-base">{item.icon}</span>
                        <span>{item.feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Elite Achievements */}
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-amber-100 shadow-sm">
                  <p className="text-[10px] font-black text-amber-700 uppercase tracking-widest mb-3">Achievements</p>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Skills', count: '12' },
                      { label: 'Projects', count: '8' },
                      { label: 'Certified', count: '5' }
                    ].map((item, i) => (
                      <div key={i} className="bg-gradient-to-br from-amber-100 to-orange-100 rounded-lg p-3 text-center border border-amber-200">
                        <p className="text-sm font-black text-amber-900">{item.count}</p>
                        <p className="text-[8px] font-bold text-amber-700 uppercase">{item.label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 13. Learning Streak Card */}
          <section className="bg-white rounded-[24px] p-8 shadow-sm border border-slate-200/60 hover:border-purple-200 transition-colors">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-orange-50 rounded-2xl flex items-center justify-center text-orange-500">
                <FireIcon className="h-6 w-6 animate-pulse" />
              </div>
              <div>
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Current Streak</h3>
                <p className="text-2xl font-black text-[#111827]">{learningStats.streak} Days</p>
              </div>
            </div>
            
            <div className="space-y-5">
              <div className="flex justify-between items-center p-3.5 bg-slate-50 rounded-2xl border border-slate-100">
                <span className="text-[10px] font-bold text-gray-500 uppercase">Longest Streak</span>
                <span className="text-base font-black text-[#111827]">28 Days</span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                 <div className="p-3.5 bg-purple-50 rounded-2xl border border-purple-100">
                    <p className="text-[9px] font-black text-[#6A0DAD] uppercase tracking-widest mb-1">XP Points</p>
                    <p className="text-lg font-black text-[#6A0DAD]">{learningStats.xp}</p>
                 </div>
                 <div className="p-3.5 bg-indigo-50 rounded-2xl border border-indigo-100">
                    <p className="text-[9px] font-black text-indigo-600 uppercase tracking-widest mb-1">Badge</p>
                    <p className="text-base font-black text-indigo-600">Silver</p>
                 </div>
              </div>
              <div className="bg-[#6A0DAD] p-4 rounded-2xl text-white text-center shadow-lg shadow-purple-200">
                 <p className="text-[10px] font-bold opacity-80 mb-0.5 uppercase tracking-wider">Next Reward</p>
                 <p className="text-base font-black text-white">1 Day Remaining</p>
              </div>
            </div>
          </section>

          {/* 12. Learning Statistics */}
          <section className="bg-white rounded-[24px] p-8 shadow-sm border border-slate-200/60 hover:border-purple-200 transition-colors">
            <h3 className="text-lg font-black text-[#111827] mb-8">Learning Stats</h3>
            <div className="space-y-5">
              {[
                { label: 'Technologies Mastered', value: completedTechCount, icon: <CheckCircleOutline className="h-4 w-4" />, color: 'text-emerald-500' },
                { label: 'Topics Completed', value: learningStats.topics, icon: <BookOpenIcon className="h-4 w-4" />, color: 'text-[#6A0DAD]' },
                { label: 'Hours Learned', value: learningStats.hours, icon: <ClockIcon className="h-4 w-4" />, color: 'text-indigo-500' },
                { label: 'AI Tutor Sessions', value: learningStats.aiSessions, icon: <SparklesIcon className="h-4 w-4" />, color: 'text-purple-400' },
                { label: 'Compiler Runs', value: learningStats.compilerRuns, icon: <CodeBracketIcon className="h-4 w-4" />, color: 'text-slate-600' },
                { label: 'Mock Interviews', value: learningStats.mockInterviews, icon: <BriefcaseIcon className="h-4 w-4" />, color: 'text-orange-400' },
                { label: 'Resume Score', value: '84%', icon: <DocumentTextIcon className="h-4 w-4" />, color: 'text-blue-500' },
              ].map((stat, i) => (
                <div key={i} className="flex items-center justify-between group">
                  <div className="flex items-center gap-3">
                    <div className={`${stat.color} opacity-60 group-hover:opacity-100 transition-opacity`}>
                      {stat.icon}
                    </div>
                    <span className="text-xs font-bold text-gray-500">{stat.label}</span>
                  </div>
                  <span className="text-xs font-black text-[#111827]">{stat.value}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 9. Bootcamp Overview (Conditional) */}
          {bootcampData && (
            <section className="bg-gradient-to-br from-indigo-900 to-[#3F0071] rounded-[24px] p-8 text-white shadow-xl relative overflow-hidden">
              <div className="relative z-10 space-y-6">
                <div className="flex items-center gap-3">
                   <AcademicCapIcon className="h-8 w-8 text-indigo-300" />
                   <h3 className="text-xl font-black">{bootcampData.bootcampName}</h3>
                </div>
                <div className="space-y-4 text-sm font-medium">
                   <div>
                      <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-1">Purpose</p>
                      <p>Become industry-ready through hands-on projects.</p>
                   </div>
                   <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-1">Phase</p>
                        <p>Phase 5</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-1">AI Review</p>
                        <p className="text-emerald-400 font-bold">94%</p>
                      </div>
                   </div>
                   <div>
                      <p className="text-[10px] font-black text-indigo-300 uppercase tracking-widest mb-1">Project</p>
                      <p className="font-bold">AI Resume Builder</p>
                   </div>
                </div>
                <div>
                  <div className="flex justify-between mb-2 text-xs font-bold">
                    <span>Progress</span>
                    <span>{bootcampData.progressPercentage || 63}%</span>
                  </div>
                  <div className="h-2 w-full bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-400" style={{ width: `${bootcampData.progressPercentage || 63}%` }}></div>
                  </div>
                </div>
                <button 
                  onClick={() => navigate('/bootcamp/my-bootcamps')}
                  className="w-full py-4 rounded-xl bg-white text-[#3F0071] font-black hover:bg-indigo-50 transition-all shadow-lg"
                >
                  Open Project Room
                </button>
              </div>
              <AcademicCapIcon className="absolute -bottom-10 -right-10 h-40 w-40 text-white/5 rotate-12" />
            </section>
          )}

          {/* 10. AI Career Insights */}
          <section className="bg-white rounded-[24px] p-8 shadow-sm border border-slate-200/60 hover:border-purple-200 transition-colors">
            <h3 className="text-lg font-black text-[#111827] mb-6 flex items-center gap-2">
               <SparklesIcon className="h-5 w-5 text-[#6A0DAD]" />
               AI Career Insights
            </h3>
            <div className="space-y-6">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Ready for:</p>
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-xs font-black text-emerald-600 bg-emerald-50 p-3 rounded-xl border border-emerald-100">
                  <CheckCircleIcon className="h-4 w-4" /> Junior React Developer
                </div>
                <div className="flex items-center gap-2 text-xs font-black text-emerald-600 bg-emerald-50 p-3 rounded-xl border border-emerald-100">
                  <CheckCircleIcon className="h-4 w-4" /> Frontend Engineer
                </div>
              </div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Expected Salary</p>
                <p className="text-xl font-black text-[#111827]">₹6–10 LPA</p>
              </div>
              <div>
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Missing Skills</p>
                <div className="flex flex-wrap gap-2">
                  {['Node.js', 'Express.js', 'Docker', 'MongoDB'].map(skill => (
                    <span key={skill} className="text-[9px] font-black bg-slate-100 text-slate-600 px-3 py-1.5 rounded-lg uppercase tracking-wider">{skill}</span>
                  ))}
                </div>
              </div>
              <button onClick={() => navigate('/student/tsg')} className="w-full py-3 rounded-xl border-2 border-[#6A0DAD] text-[#6A0DAD] font-black hover:bg-purple-50 transition-all text-sm">
                Generate Resume
              </button>
            </div>
          </section>

          {/* 14. Smart Notifications */}
          <section className="bg-white rounded-[24px] p-8 shadow-sm border border-slate-200/60 hover:border-purple-200 transition-colors">
             <div className="flex items-center justify-between mb-8">
                <h3 className="text-lg font-black text-[#111827]">Notifications</h3>
                <div className="w-8 h-8 bg-purple-50 rounded-full flex items-center justify-center text-[#6A0DAD] relative">
                   <BellIcon className="h-4 w-4" />
                   <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full border-2 border-white"></span>
                </div>
             </div>
             <div className="space-y-6">
                {[
                  { text: 'Your Bootcamp deadline is approaching.', type: 'alert' },
                  { text: 'React Level 8 unlocked.', type: 'success' },
                  { text: 'Node.js is recommended next.', type: 'info' },
                  { text: '2 new interview questions added.', type: 'info' }
                ].map((note, i) => (
                  <div key={i} className="flex gap-4 group">
                     <div className="w-1.5 h-auto bg-purple-100 group-hover:bg-[#6A0DAD] transition-colors rounded-full"></div>
                     <p className="text-xs font-bold text-gray-600 leading-snug">{note.text}</p>
                  </div>
                ))}
             </div>
          </section>
          
          {/* 11. Smart Recommendations */}
          <section className="bg-[#3F0071] rounded-[24px] p-8 text-white">
             <h3 className="text-xl font-black mb-6 text-white">Smart Suggestions</h3>
             <ul className="space-y-4">
                {[
                  'Finish React Hooks today.',
                  'Learn Node.js next.',
                  'Take a Mock Interview.',
                  'Complete your Bootcamp Phase.',
                  'Revise JavaScript Closures.',
                  'Practice SQL Queries.'
                ].map((s, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm font-bold text-white hover:text-purple-200 transition-colors cursor-pointer group">
                     <ChevronRightIcon className="h-4 w-4 text-purple-400 group-hover:translate-x-1 transition-transform" />
                     {s}
                  </li>
                ))}
             </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TenantDashboardHome;


